import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Typography,
  IconButton,
} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {enqueueSnackbar} from "notistack";
import {logError} from "../../shared/services/logger.service";
import {CustomOperationMethodDto, CreateCustomOperationMethodDto} from "./dtos/operation-method.dto";
import {createOperation, deleteOperation, getOperation, updateOperation} from "./services/operation-method.service";
import {useToolbar} from "../../components/ToolbarContext";
import {getAllEquipment} from "./services/equipment.service";
import {EquipmentDto} from "./dtos/equipment.dto";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
type BehaviorType = 'SelectedByDefault' | 'AvailableInList';

function OperationMethodDetailPage() {
  const navigate = useNavigate();
  const {operationMethodId} = useParams();
  const toolbarContext = useToolbar();
  const [, setSavedOperation] = useState<CustomOperationMethodDto | null>(null);
  const [changedOperation, setChangedOperation] = useState<Partial<CustomOperationMethodDto>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [equipment, setEquipment] = useState<EquipmentDto[]>([]);

  const isExistingOperation = operationMethodId && !Number.isNaN(parseInt(operationMethodId));

  useEffect(() => {
    toolbarContext.initToolbar('OperationMethodDetailPage', operationMethodId ? 'Edit Operation Method' : 'New Operation Method');
    fetchEquipment();
    if (isExistingOperation) {
      fetchOperation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationMethodId]);

  async function fetchEquipment() {
    try {
      const equipment = await getAllEquipment(navigate);
      setEquipment(equipment);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logError('Error fetching equipment', {}, error as Error);
      enqueueSnackbar(`Error fetching equipment: ${errorMessage}`, {variant: 'error', autoHideDuration: 5000});
    }
  }

  async function fetchOperation() {
    if (!isExistingOperation) return;

    setIsLoading(true);
    try {
      const fetchedOperation = await getOperation(navigate, parseInt(operationMethodId));
      setSavedOperation(fetchedOperation);
      setChangedOperation(fetchedOperation);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logError('Error fetching operation', {}, error as Error);
      enqueueSnackbar(`Error fetching operation: ${errorMessage}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setIsLoading(false);
    }
  }

  const handleNameChange = (newValue: string | null | undefined) => {
    const matchingEquipment = equipment.find(e => e.nickname === newValue);
    console.log('matchingEquipment', matchingEquipment);
    setChangedOperation({...changedOperation, name: newValue ?? '', linkedEquipmentId: matchingEquipment?.id});
  }

  const handleSave = async () => {
    if (!changedOperation.name?.trim()) {
      enqueueSnackbar('Operation method name is required', {variant: 'error', autoHideDuration: 5000});
      return;
    }

    if (!changedOperation.behavior) {
      enqueueSnackbar('Behavior is required', {variant: 'error', autoHideDuration: 5000});
      return;
    }

    setIsLoading(true);
    try {
      if (isExistingOperation) {
        await updateOperation(navigate, parseInt(operationMethodId), changedOperation);
      } else {
        const createDto: CreateCustomOperationMethodDto = {
          name: changedOperation.name,
          behavior: changedOperation.behavior,
          linkedEquipmentId: changedOperation.linkedEquipmentId
        };
        await createOperation(navigate, createDto);
      }
      navigate('/equipment/operation-methods');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logError('Error saving operation', {}, error as Error);
      enqueueSnackbar(`Error saving operation: ${errorMessage}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isExistingOperation) return;
    setIsLoading(true);
    try {
      await deleteOperation(navigate, parseInt(operationMethodId));
      navigate('/equipment/operation-methods');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      logError('Error deleting operation', {}, error as Error);
      enqueueSnackbar(`Error deleting operation: ${errorMessage}`, {variant: 'error', autoHideDuration: 5000});
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, p: 3, maxWidth: 600, mx: 'auto', mt: 3}}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate('/equipment/operation-methods')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          {isExistingOperation ? 'Edit Operation Method' : 'Add New Operation Method'}
        </Typography>
      </Box>
      
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        <Autocomplete
          freeSolo
          options={equipment.map(e => e.nickname)}
          value={changedOperation.name || ''}
          onChange={(_, newValue) => {
            handleNameChange(newValue);
          }}
          inputValue={changedOperation.name || ''}
          onInputChange={(_, newInputValue) => {
            setChangedOperation({...changedOperation, name: newInputValue ?? ''});
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Operation Method Name"
              required
              helperText="Start typing to search for equipment nicknames or enter a custom operation method name"
            />
          )}
        />

        <FormControl fullWidth>
          <InputLabel>Behavior *</InputLabel>
          <Select
            value={changedOperation.behavior ?? ''}
            label="Default Selection"
            onChange={(e) => setChangedOperation({...changedOperation, behavior: e.target.value as BehaviorType})}
          >
            <MenuItem value="SelectedByDefault">Selected by default</MenuItem>
            <MenuItem value="AvailableInList">Available in list</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2}}>
        {isExistingOperation && (
          <Button onClick={handleDelete} color="error" disabled={isLoading}>
            Delete
          </Button>
        )}
        <Button onClick={() => navigate('/equipment/operation-methods')} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          Save
        </Button>
      </Box>
    </Box>
  );
}

export default OperationMethodDetailPage; 